// Fixed HUD for the farm: level badge + XP bar (top centre) and the bottom button strip.
// The bottom strip sits at centre so the touch shell's joystick (bottom-left) and action
// buttons (bottom-right) stay clear.
import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { gameState } from '../state/gameState.js';
import { UI_DEPTH } from '../ui/ySort.js';
import { storageCount } from './farmStorage.mjs';
import { gameButton, gameProgressBar, hudBadge } from '../ui/gameUi.js';
import { pinToCamera } from '../ui/pin.mjs';
import { iconText } from '../ui/iconText.js';
import { doubleXpMinutes } from './popupQueue.mjs';
import { serverNow } from '../state/serverClock.mjs';
import { installPanelSwitch, switchButton } from '../ui/panelSwitch.js';

const FONT = 'system-ui, -apple-system, sans-serif';

// A wooden 3D button fixed to the screen (gameUi.js).
// `switchId`: tapped with a window open, it closes the window and does its job in the same tap (ui/panelSwitch.js).
function button(scene, x, y, w, label, onClick, switchId = null) {
  let click = onClick;
  const b = gameButton(scene, x, y, w, 48, label, () => click(), { variant: 'wood', fontSize: 17 }).setScrollFactor(0).setDepth(UI_DEPTH);
  if (switchId) click = switchButton(scene, b, switchId, onClick).onClick;
  // Greyed by look only: a tap still reaches onClick, which explains why (e.g. "poz. 12").
  return { setLabel: (s) => b.setLabel(s), setEnabled: (on) => b.setVariant(on ? 'wood' : 'disabled'), destroy: () => b.destroy() };
}

// The "Dom" button's spot in the bottom strip (a friend's farm has it in the same place: VisitFarmScene).
export const DOM_BUTTON = { x: GAME_WIDTH / 2 - 515, y: GAME_HEIGHT - 40, w: 120, h: 48 };

export class FarmHud {
  constructor(scene, { onMarket, onExpand, onHome, onStorage, getSeed }) {
    this.scene = scene;
    this.getSeed = getSeed;
    const cx = GAME_WIDTH / 2;
    // Level badge + XP bar
    ({ bg: this.levelBg, text: this.levelText } = hudBadge(scene, { depth: UI_DEPTH }));
    this.bar = pinToCamera(gameProgressBar(scene, cx, 44, 284, 14)).setDepth(UI_DEPTH + 1);
    // The welcome-back hour, under the badge: double XP and the minutes left (owner 2026-10-16).
    this.x2Bg = scene.add.graphics().setScrollFactor(0).setDepth(UI_DEPTH).setVisible(false);
    this.x2Bg.fillStyle(0x6e4220, 1).fillRoundedRect(cx - 90, 60, 180, 30, 13);
    this.x2Bg.fillStyle(0xf2c443, 1).fillRoundedRect(cx - 87, 62, 174, 26, 11);
    this.x2Text = iconText(scene, cx, 75, '', { fontFamily: FONT, fontSize: '16px', color: '#5a3414', fontStyle: 'bold' })
      .setOrigin(0.5).setScrollFactor(0).setDepth(UI_DEPTH + 1).setVisible(false);
    this._x2Timer = scene.time.addEvent({ delay: 10000, loop: true, callback: () => this._refreshX2() });
    // Bottom strip
    const y = GAME_HEIGHT - 40;
    installPanelSwitch(scene);
    // The middle stays free for the chat pill (ui/ChatInput.js) that floats at bottom centre.
    this.homeBtn = button(scene, DOM_BUTTON.x, y, DOM_BUTTON.w, '{house} Dom', onHome, 'home');
    this.storageBtn = button(scene, cx - 360, y, 170, '{crate} Schowek', onStorage ?? (() => {}), 'storage');
    // Always just "Rynek" (owner 2026-10-02): the seed being planted is shown on the Siewnik tile (FarmToolStrip)
    this.seedBtn = button(scene, cx - 158, y, 210, '{cart} Rynek', onMarket, 'market');
    this.expandBtn = button(scene, cx + 178, y, 230, '{plus} Powiększ', onExpand);
    this._unsub = gameState.on(() => this.refresh());
    scene.events.once('shutdown', () => this.destroy());
    this.refresh();
  }

  refresh() {
    const raw = gameState.raw;
    if (!raw) return;
    const lp = raw.level_progress ?? { level: raw.level ?? 1, xp_into_level: 0, xp_for_level: 10 };
    this.levelText.setText(`{xp_star} Poziom ${lp.level}   ${lp.xp_into_level} / ${lp.xp_for_level} XP`);
    this.bar.set(lp.xp_into_level / Math.max(1, lp.xp_for_level));
    this._refreshX2();
    const n = storageCount(raw);
    this.storageBtn.setLabel(n > 0 ? `{crate} Schowek (${n})` : '{crate} Schowek');
    const nx = raw.farm?.next_expansion;
    if (!nx) { this.expandBtn.setLabel('{plus} Największa farma'); this.expandBtn.setEnabled(false); }
    else {
      this.expandBtn.setLabel(`{plus} ${nx.size}×${nx.size}  ${nx.cost.toLocaleString('pl-PL')} {coin}  poz. ${nx.min_level}`);
      this.expandBtn.setEnabled((raw.level ?? 1) >= nx.min_level && raw.money >= nx.cost);
    }
  }

  _refreshX2() {
    const min = doubleXpMinutes(gameState.raw?.farm, serverNow());
    this.x2Bg.setVisible(min != null);
    this.x2Text.setVisible(min != null);
    if (min != null) this.x2Text.setText(`{xp_star} x2 XP  ${min} min`);
  }

  destroy() {
    this._unsub?.(); this._unsub = null;
    this._x2Timer?.remove(); this._x2Timer = null;
    for (const o of [this.levelBg, this.levelText, this.bar, this.x2Bg, this.x2Text]) o.destroy();
    for (const b of [this.homeBtn, this.seedBtn, this.storageBtn, this.expandBtn]) b.destroy();
  }
}
